<script setup>
import { useHead, useRoute, useScrollTopOnMounted } from '#imports';
import { getCommonMetaTags } from '~/utils/get-common-meta-tags';

useScrollTopOnMounted();

const route = useRoute();

const title = 'О нас — DOXA';
const description =
  'Мы верим, что войны, коррупция и угнетение не неизбежны, что возможно будущее без них — со свободой самоопределения и честным распределением ресурсов.';

useHead({
  title,
  meta: getCommonMetaTags({
    path: route.fullPath,
    ogType: 'website',
    title,
    description,
  }),
});
</script>

<template>
  <!--  @TODO: create page in sanity-->
  <LayoutDefaultPage
    is-animated-header
    is-header-shown
    is-hide-donation-form
    is-transparent-header
  >
    <div class="about">
      <div class="about__header">
        <div class="about__header-content content-container">
          <img
            alt="about title"
            class="about__title"
            src="~/assets/images/about-title.svg"
          />
        </div>
      </div>

      <div class="about__content content-container">
        <div class="about__description-wrapper">
          <div class="about__description">
            <picture class="about__illustration">
              <source
                media="(min-resolution: 192dpi)"
                srcset="/images/illustrations/illustration-1@2x.png"
              />

              <img
                alt="illustration"
                loading="lazy"
                src="/images/illustrations/illustration-1.png"
              />
            </picture>
            <p>
              DOXA пишет, снимает и&nbsp;говорит о&nbsp;самых важных
              проблемах&nbsp;&mdash; о&nbsp;войне и диктатуре, неравенстве
              и&nbsp;климатическом кризисе. Мы&nbsp;стремимся создавать
              пространство, где все могут обсуждать эти проблемы и&nbsp;думать,
              как изменить мир к&nbsp;лучшему. А&nbsp;ещё мы&nbsp;выпускаем
              полезные материалы о&nbsp;том, как выжить&nbsp;&mdash; как
              справиться с&nbsp;повседневными последствиями катастроф.
              Мы&nbsp;даем доступ к&nbsp;информации, необходимой, чтобы
              действовать.
            </p>
            <picture class="about__illustration">
              <source
                media="(min-resolution: 192dpi)"
                srcset="/images/illustrations/illustration-2@2x.png"
              />

              <img
                alt="illustration"
                loading="lazy"
                src="/images/illustrations/illustration-2.png"
              />
            </picture>
            <p>
              Семь лет назад DOXA запустилась как студенческий журнал, который
              рассказывал про проблемы российских университетов. Мы&nbsp;спорили
              о том, как должен быть устроен университет, чтобы в&nbsp;нём можно
              было свободно и&nbsp;открыто обсуждать идеи, получать достойное
              образование, делать осмысленные исследования без страха
              за&nbsp;свою жизнь, свободу и&nbsp;карьеру.
            </p>
            <picture class="about__illustration">
              <source
                media="(min-resolution: 192dpi)"
                srcset="/images/illustrations/illustration-3@2x.png"
              />

              <img
                alt="illustration"
                loading="lazy"
                src="/images/illustrations/illustration-3.png"
              />
            </picture>
            <p>
              Все эти годы мы&nbsp;видели, как государство уничтожает остатки
              политических свобод студенто:к и&nbsp;преподаватель:ниц
              и&nbsp;превращает лучшие российские университеты в&nbsp;рупоры
              пропаганды.
            </p>
            <picture class="about__illustration">
              <source
                media="(min-resolution: 192dpi)"
                srcset="/images/illustrations/illustration-4@2x.png"
              />

              <img
                alt="illustration"
                loading="lazy"
                src="/images/illustrations/illustration-4.png"
              />
            </picture>
            <p>
              Мы&nbsp;сопротивлялись авторитарным тенденциям в&nbsp;российских
              вузах и давали голос сопротивляющимся активист:кам
              и&nbsp;движениям.
            </p>
            <p>
              Мы&nbsp;верим, что войны, коррупция и&nbsp;угнетение
              не&nbsp;неизбежны, что возможно будущее без них&nbsp;&mdash;
              со&nbsp;свободой самоопределения и&nbsp;со справедливым
              распределением ресурсов. Несмотря на&nbsp;цензуру и
              государственный террор против независимых СМИ, мы&nbsp;продолжаем
              честно выполнять свою работу и&nbsp;называть вещи своими именами
            </p>
            <p class="about__description-paragraph--bold">
              Присоединяйтесь к нам!
            </p>
          </div>
          <picture class="about__side-illustration">
            <source
              media="(min-resolution: 192dpi)"
              srcset="/images/illustrations/full-illustration@2x.png"
            />

            <img
              alt="illustration"
              loading="lazy"
              src="/images/illustrations/full-illustration.png"
            />
          </picture>
        </div>
      </div>
      <div class="about__payment-block-wrapper">
        <DonationBlockPaymentWrapper
          description="Мы работаем только для наших читатель:ниц.
            Вы можете помочь нам и оформить ежемесячное или разовое пожертвование
          "
        >
          <img
            alt="donation title"
            src="~/assets/images/donation-title-2.svg"
          />
        </DonationBlockPaymentWrapper>
      </div>
      <div class="about__authors-wrapper">
        <AboutAuthors />
      </div>
    </div>
  </LayoutDefaultPage>
</template>

<style lang="postcss" scoped>
.about {
  display: flex;
  flex-direction: column;

  &__header {
    height: 382px;
    margin: calc(var(--main-header-height) * -1) 0 0;
    border-radius: 0 0 24px 24px;
    background: linear-gradient(-90deg, #6c2179 0%, #b473c0 100%);
  }

  &__header-content {
    display: flex;
    align-items: flex-end;
    height: 100%;
  }

  &__title {
    width: 100%;
    max-width: 650px;
    max-height: 177px;
    margin: 0 0 -3px;
  }

  &__content {
    padding-top: 60px;
    font-family: var(--body-font-family);
  }

  &__description-wrapper {
    display: grid;
    grid-template-columns: 728px 1fr;
  }

  &__description {
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
    font-weight: 500;
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  &__description-paragraph {
    &--bold {
      font-weight: 700;
    }
  }

  &__illustration {
    display: none;
  }

  &__side-illustration {
    justify-self: center;
    max-width: 400px;
    min-width: 300px;
    align-self: center;
  }

  &__side-illustration > img {
    width: 100%;
  }

  &__payment-block-wrapper {
    padding: 100px 0 80px 0;
  }

  &__authors-wrapper {
    padding-bottom: 60px;
  }

  @media (--small-vp) {
    &__header {
      height: 240px;
    }

    &__title {
      max-width: 314px;
      max-height: 73px;
    }

    &__content {
      padding-top: 40px;
    }

    &__description {
      font-size: 16px;
      line-height: 22px;
      gap: 16px;
    }

    &__payment-block-wrapper {
      padding: 32px 0 40px 0;
    }

    &__authors-wrapper {
      padding-bottom: 62px;
    }
  }

  @media (--tablet-vp) {
    &__payment-block-wrapper {
      padding: 32px 0 40px 0;
    }

    &__side-illustration {
      display: none;
    }

    &__illustration {
      display: block;
      align-self: center;
      max-width: 450px;

      & > img {
        max-width: 100%;
        height: auto;
        margin: -16px 0;
      }
    }

    &__description-wrapper {
      grid-template-columns: 1fr;
    }
  }
}
</style>
